<script setup lang="ts">
    import { ref, toRaw, unref } from 'vue';
    import Modal, { ModalSize } from '../Modal.vue';
    import { usePostFavoriteFilter } from '@/js/hooks/favoriteFilter';
    import TextInput from '../TextInput.vue';
    import { __ } from '@/js/stores/expressions';
    import SuccessCheck from '../SuccessCheck.vue';
    import EchoButton from '../EchoButton.vue';
    import { useFilterStoreByName } from '@/js/stores/filter';
    import { storeToRefs } from 'pinia';
    import { EchoFilterRequest, FilterStoreName } from '@/js/services/api/filter/types';

    const props = defineProps<{
        show: boolean;
        filterStoreName: FilterStoreName;
    }>();

    const filterStore = useFilterStoreByName(props.filterStoreName);

    const { requestData } = storeToRefs(filterStore);

    const unwrapFilter = () => {
        const raw = toRaw(unref(requestData));
        return JSON.parse(JSON.stringify(raw)) as EchoFilterRequest;
    };

    const emit = defineEmits<{
        (e: 'close'): void;
    }>();

    const { mutateAsync, isSuccess, isPending, reset } = usePostFavoriteFilter();

    const submit = async () => {
        const data = await mutateAsync({
            name: filterName.value.trim(),
            favoriteFilter: unwrapFilter(),
        });
        filterStore.setSelectedFavoriteFilter({ id: data.id, name: data.name });
    };

    const close = () => {
        reset();
        filterName.value = '';
        emit('close');
    };

    const filterName = ref<string>('');
</script>
<template>
    <Modal :size="ModalSize.Medium" :show="show" @close="close()">
        <template v-if="!isSuccess">
            <div class="flex flex-col justify-end">
                <form @submit.prevent="submit">
                    <h2 class="text-2xl text-dark font-semibold">
                        {{ __('name-favorite-filter') }}
                    </h2>
                    <p class="text-sm text-gray-medium">
                        {{ __('name-favorite-filter-description') }}
                    </p>
                    <TextInput
                        v-model="filterName"
                        :aria-label="__('name-favorite-filter')"
                        :placeholder="__('favorite-filters')"
                        class="mt-6 mb-6 w-full"
                    />
                    <div class="flex justify-end">
                        <EchoButton :loading="isPending" :disabled="!filterName" type="submit">
                            {{ __('save') }}
                        </EchoButton>
                    </div>
                </form>
            </div>
        </template>
        <template v-else>
            <div class="flex flex-col justify-center">
                <SuccessCheck :message="__('name-favorite-filter-success')" />
            </div>
        </template>
    </Modal>
</template>
